import { currencyFormatter } from '@/lib/utils'
import { AlertCircleIcon, Award } from 'lucide-react'
import type { Purse } from '@/queries/program_purses/schemas.ts'
import { FundAwardedStudentsAction } from '../fund-awarded-students/fund-awarded-students-action'

type ScholarshipAwardCardProps = {
  purse: Purse
  programId: number
}

export function ScholarshipAwardCard({
  purse,
  programId,
}: ScholarshipAwardCardProps) {
  const awardedStudentsCount = purse.awarded_students_count

  // Only an explicit zero blocks funding. An API that does not report the count yet leaves it
  // undefined, and the preview inside the dialog is then the source of truth.
  const disabledReason =
    awardedStudentsCount === 0
      ? 'No students are in Awarded status on this purse.'
      : null

  return (
    <section
      className="bg-white rounded-lg w-full shadow-sm border border-gray-200 p-6 flex flex-col gap-4 mx-auto"
      aria-label={`Fund awarded students for ${purse.name}`}
    >
      <header className="flex items-center gap-2">
        <Award className="size-5 text-primary" aria-hidden="true" />
        <h2 className="text-xl font-semibold text-gray-900">{purse.name}</h2>
      </header>

      <p className="text-sm text-gray-700">
        Every student in <span className="font-medium">Awarded</span> status on
        this purse will be funded with the scholarship amount ServiceNow
        supplied. Award amounts cannot be edited here.
      </p>

      <div className="bg-gray-50 p-4 rounded-lg space-y-2">
        {awardedStudentsCount !== undefined && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Awaiting Funding</span>
            <span className="font-medium">
              {awardedStudentsCount} student
              {awardedStudentsCount === 1 ? '' : 's'}
            </span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Distributed</span>
          <span className="font-medium text-red-600">
            {currencyFormatter.format(purse.balance_distribuited)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Available Balance</span>
          <span className="font-medium text-green-600">
            {currencyFormatter.format(purse.balance_available)}
          </span>
        </div>
      </div>

      <div className="mt-auto space-y-4">
        <div className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-md p-3">
          <AlertCircleIcon
            className="mt-0.5 shrink-0 text-yellow-500"
            aria-hidden="true"
          />
          <p className="text-sm text-gray-700">
            Funding is safe to retry: students already funded for this purse are
            skipped and never funded twice. If the awarded total exceeds the
            available balance, nothing is funded.
          </p>
        </div>

        <FundAwardedStudentsAction
          purse={purse}
          programId={programId}
          variant="primary"
          size="md"
          className="w-full"
          disabledReason={disabledReason}
        />
      </div>
    </section>
  )
}
